import type { Edge, Node } from '@xyflow/react';
import type { InternalNetworkAssessmentEntityActionGraphDto } from '@terra/shared-contracts-internal-network';
import type {
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';

const ENTITY_COLUMN_WIDTH = 300;

export const resolveFrontierEntityId = (
  graph: InternalNetworkAssessmentEntityActionGraphDto,
): string | undefined => {
  const lastHopTarget = [...graph.actions]
    .filter((action) => {
      const target = graph.entities.find(
        (entity) => entity.id === action.targetEntityId,
      );

      return (
        target?.kind !== 'GOAL' &&
        (action.status === 'SUCCEEDED' || action.status === 'IN_PROGRESS')
      );
    })
    .sort((left, right) =>
      (left.occurredAt ?? '').localeCompare(right.occurredAt ?? ''),
    )
    .at(-1)?.targetEntityId;

  if (lastHopTarget) {
    return lastHopTarget;
  }

  return graph.entities.find((entity) => entity.status === 'OWNED')?.id;
};

const buildGapNodeData = ({
  column,
}: {
  column: number;
}): AttackPathEventNodeData => ({
  role: 'UNKNOWN_GAP',
  campaignId: null,
  campaignStatus: 'PLANNED',
  isCampaignRunning: false,
  chainProgress: 0,
  kind: 'IMPACT_ACHIEVED',
  typeLabel: 'UNKNOWN_GAP',
  title: '?',
  subtitle: null,
  caption: null,
  weaknessId: null,
  mitreTechniqueId: null,
  provenance: null,
  privilegeTier: null,
  resourceKind: null,
  severity: null,
  status: 'IN_PROGRESS',
  isProven: false,
  exploitsWeakness: false,
  isCrownJewel: false,
  isDomainController: false,
  impacts: [],
  elapsedSeconds: column,
  occurredAt: new Date(0).toISOString(),
  hopIndex: column,
  retryCount: 0,
  findingId: null,
  hostname: null,
  entityKind: null,
  entityId: null,
  objective: null,
  signalId: null,
  primaryExecutionId: null,
  durationSeconds: null,
  chainStatus: 'IN_PROGRESS',
  column,
  lane: 0,
});

const buildHypothesizedEdge = ({
  id,
  source,
  target,
}: {
  id: string;
  source: string;
  target: string;
}): Edge<AttackPathEventEdgeData> => ({
  id,
  source,
  target,
  type: 'attackPathEventEdge',
  data: {
    label: null,
    variant: 'hypothesized',
    campaignId: null,
    status: 'IN_PROGRESS',
    isCampaignRunning: false,
  },
});

export const appendUnreachedGoalGaps = ({
  graph,
  columnByEntityId,
}: {
  graph: InternalNetworkAssessmentEntityActionGraphDto;
  columnByEntityId: Map<string, number>;
}): {
  nodes: Node<AttackPathEventNodeData>[];
  edges: Edge<AttackPathEventEdgeData>[];
} => {
  const frontierEntityId = resolveFrontierEntityId(graph);

  if (!frontierEntityId) {
    return { nodes: [], edges: [] };
  }

  const frontierColumn = columnByEntityId.get(frontierEntityId) ?? 0;
  const reachedGoalIds = new Set(
    graph.actions
      .filter((action) => {
        const target = graph.entities.find(
          (entity) => entity.id === action.targetEntityId,
        );

        return (
          action.status === 'SUCCEEDED' &&
          target?.kind === 'GOAL' &&
          target.status === 'SUCCEEDED'
        );
      })
      .map((action) => action.targetEntityId),
  );

  return graph.entities
    .filter(
      (entity) =>
        entity.kind === 'GOAL' &&
        entity.status !== 'SUCCEEDED' &&
        !reachedGoalIds.has(entity.id),
    )
    .reduce<{
      nodes: Node<AttackPathEventNodeData>[];
      edges: Edge<AttackPathEventEdgeData>[];
    }>(
      (elements, goal) => {
        const gapId = `unknown-gap:${goal.id}`;
        const gapColumn = frontierColumn + 1;
        const goalColumn = frontierColumn + 2;

        columnByEntityId.set(gapId, gapColumn);
        columnByEntityId.set(goal.id, goalColumn);

        return {
          nodes: [
            ...elements.nodes,
            {
              id: gapId,
              type: 'attackPathEventNode',
              position: {
                x: gapColumn * ENTITY_COLUMN_WIDTH,
                y: 0,
              },
              data: {
                ...buildGapNodeData({ column: gapColumn }),
                lane: 0,
              },
            },
          ],
          edges: [
            ...elements.edges,
            buildHypothesizedEdge({
              id: `edge:gap:${goal.id}:in`,
              source: frontierEntityId,
              target: gapId,
            }),
            buildHypothesizedEdge({
              id: `edge:gap:${goal.id}:out`,
              source: gapId,
              target: goal.id,
            }),
          ],
        };
      },
      { nodes: [], edges: [] },
    );
};
